import {
  Landmark, PieChart, Users, Wallet,
  GraduationCap, PiggyBank, ShieldCheck, FileText, ArrowUpRight
} from 'lucide-react';

export default function Services({ darkMode, scrollToSection }) {
  const services = [
    {
      title: 'Financial Advice',
      description: 'At RiseCap, we offer personalized financial planning tailored to your unique needs and goals. Our team provides expert guidance on goal-setting to ensure your long-term financial security, whether it\'s planning for retirement, your children\'s education, or other important life milestones.',
      icon: <Landmark size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
    {
      title: 'Portfolio Management',
      description: 'Our approach emphasizes diversification, ensuring that your investments are spread across various asset classes to minimize risks and maximize returns. We also provide ongoing portfolio monitoring and adjustment, ensuring your investments stay aligned with your changing goals and market conditions.',
      icon: <PieChart size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
    {
      title: 'Recruitment',
      description: 'RiseCap offers exciting recruitment opportunities for individuals passionate about guiding others in their financial journey. We\'re building a team of service-oriented professionals. Our environment ensures no targets, no pressure, and provides full team support, respect, and opportunities for international travel and personal growth.',
      icon: <Users size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
    {
      title: 'Cash Flow Assistance',
      description: 'Effective budgeting and cash flow management are essential to achieving financial stability. Our services include providing expert advice on managing income and expenses, helping you live within your means while saving for future goals.',
      icon: <Wallet size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
    {
      title: 'Child Future Planning',
      description: 'At RiseCap, we understand that every parent dreams of a secure and prosperous future for their child. Our Child Future Planning service helps you prepare financially for key milestones such as education, extracurricular development, and life goals, ensuring their aspirations are never limited by finances.',
      icon: <GraduationCap size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
    {
      title: 'Saving Plans',
      description: 'Whether you\'re saving for short-term goals like a vacation or long-term objectives such as buying a home or funding your children\'s education, RiseCap is here to help. We guide you in setting up effective saving plans and provide strategies to help you stay on track.',
      icon: <PiggyBank size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
    {
      title: 'Term Plans (Life Insurance)',
      description: 'Protecting your family\'s financial future is a top priority. We assist you in choosing the right life insurance policies, ensuring that your loved ones are financially secure in the event of an unexpected tragedy.',
      icon: <ShieldCheck size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
    {
      title: 'Tax & Retirement Planning',
      description: 'We help you plan for retirement by estimating your future financial needs and providing strategies that include the right retirement accounts, tax-saving methods, and investment options to ensure a stress-free post-retirement life.',
      icon: <FileText size={24} />,
      accent: 'from-[#C9A84C]/20 to-transparent',
    },
  ];

  return (
    <section id="services" className={`py-24 transition-colors duration-300
      ${darkMode ? 'bg-[#0A0F1E]' : 'bg-[#F8F6F1]'}`}>
      <div className="container mx-auto px-4 lg:px-16">

        {/* Section Header */}
        <div className="mb-16 text-center">
          <p className={`text-xs font-medium tracking-[0.3em] uppercase mb-3
            ${darkMode ? 'text-[#C9A84C]/70' : 'text-[#8B6914]'}`}>
            What We Offer
          </p>
          <h2 className={`text-4xl lg:text-5xl font-bold ${darkMode ? 'text-white' : 'text-[#0A0F1E]'}`}>
            Our <span className="text-[#C9A84C]">Services</span>
          </h2>
          <div className="w-16 h-0.5 bg-[#C9A84C] mx-auto mt-5"></div>
          <p className={`mt-5 max-w-xl mx-auto text-sm leading-relaxed
            ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Comprehensive financial solutions designed around your life goals — from protection to growth.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {services.map((service, index) => (
            <div
              key={index}
              onClick={() => scrollToSection('contact')}
              className={`group cursor-pointer p-6 rounded-2xl border relative overflow-hidden transition-all duration-300
                hover:-translate-y-1 hover:shadow-xl
                ${darkMode
                  ? 'bg-[#0D1525] border-[#C9A84C]/10 hover:border-[#C9A84C]/40 hover:shadow-[#C9A84C]/5'
                  : 'bg-white border-[#C9A84C]/15 hover:border-[#C9A84C]/40 hover:shadow-[#C9A84C]/10'
                }`}
            >
              {/* Top gradient accent */}
              <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${service.accent} opacity-0 group-hover:opacity-100 transition-opacity`}></div>

              {/* Icon */}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-colors
                ${darkMode
                  ? 'bg-[#C9A84C]/10 text-[#C9A84C] group-hover:bg-[#C9A84C] group-hover:text-[#0A0F1E]'
                  : 'bg-[#C9A84C]/15 text-[#8B6914] group-hover:bg-[#C9A84C] group-hover:text-[#0A0F1E]'
                }`}>
                {service.icon}
              </div>

              <h3 className={`text-base font-semibold mb-3 transition-colors
                ${darkMode
                  ? 'text-white group-hover:text-[#C9A84C]'
                  : 'text-[#0A0F1E] group-hover:text-[#8B6914]'
                }`}>
                {service.title}
              </h3>

              <p className={`text-xs leading-relaxed mb-4
                ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {service.description}
              </p>

              {/* Learn more arrow */}
              <div className={`flex items-center gap-1 text-xs font-medium transition-colors
                ${darkMode ? 'text-[#C9A84C]/50 group-hover:text-[#C9A84C]' : 'text-[#C9A84C]/60 group-hover:text-[#8B6914]'}`}>
                Enquire now
                <ArrowUpRight size={12} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}